import React, { useEffect, useRef, useState } from "react";
import { PlaySolidIcon } from "@fluentui/react-icons-mdl2";
import {
  getCachedInstanceTile,
  renderInstanceThumbnail,
} from "../../helpers/CornerstoneThumbnailService";
import "../../styles/styles.css";

// Grid width (px) at and above which tiles go two-up; below it, one column
// (e.g. the 150px panel while the report pane is open).
const TWO_COLUMN_MIN_WIDTH = 180;

const PencilSVG = () => (
  <svg width="14" height="14" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0.998047 14.998H3.91431L12.5153 6.39702L9.59908 3.48076L0.998047 12.0818V14.998ZM2.55339 12.7273L9.59908 5.68156L10.3145 6.39702L3.26884 13.4427H2.55339V12.7273Z" fill="#4da6ff" />
    <path d="M12.9508 1.22552C12.6475 0.922224 12.1576 0.922224 11.8543 1.22552L10.4312 2.64865L13.3474 5.56491L14.7706 4.14178C15.0739 3.83849 15.0739 3.34855 14.7706 3.04526L12.9508 1.22552Z" fill="#4da6ff" />
  </svg>
);

const StarSVG = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 12.8589L12.944 16L11.632 10.08L16 6.09684L10.248 5.58316L8 0L5.752 5.58316L0 6.09684L4.368 10.08L3.056 16L8 12.8589Z" fill="#4da6ff" />
  </svg>
);

/** One instance tile: lazily rendered first frame + number / ▶ / ✏ / ★ badges. */
const InstanceTile = ({ imageId, number, isClip, frames, hasPR, hasKO, active, onClick }) => {
  const ref = useRef(null);
  const [src, setSrc] = useState(() => getCachedInstanceTile(imageId));

  // Keep the image shown in the active viewport visible inside the grid's own
  // scroll area as the user moves through the series.
  useEffect(() => {
    if (active && ref.current?.scrollIntoView) {
      ref.current.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  }, [active]);

  useEffect(() => {
    if (src || !ref.current) return undefined;
    let cancelled = false;
    // Render only once the tile scrolls into the (clipped) panel view.
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      observer.disconnect();
      renderInstanceThumbnail(imageId).then((url) => {
        if (!cancelled && url) setSrc(url);
      });
    }, { rootMargin: "100px" });
    observer.observe(ref.current);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [imageId, src]);

  return (
    <div
      ref={ref}
      className={`instance-tile${active ? " instance-tile-active" : ""}`}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title={`Image ${number}${isClip ? ` · ${frames} frames` : ""}`}
      data-testid={`instance-tile-${number}`}
    >
      {src ? <img src={src} alt="" draggable={false} /> : <div className="instance-tile-placeholder" />}
      <span className="instance-tile-number">{number}</span>
      {isClip && (
        <span className="instance-tile-badge instance-tile-play" data-testid="instance-tile-play">
          <PlaySolidIcon />
        </span>
      )}
      {hasPR && (
        <span className="instance-tile-badge instance-tile-pr" data-testid="instance-tile-pr">
          <PencilSVG />
        </span>
      )}
      {hasKO && (
        <span className="instance-tile-badge instance-tile-ko" data-testid="instance-tile-ko">
          <StarSVG />
        </span>
      )}
    </div>
  );
};

/**
 * Per-instance grid shown under an expanded series thumbnail (MedDream-style
 * series panel): one tile per instance, two-up when the panel is wide, one
 * column when it is narrow. Clicking a tile shows that instance.
 */
const InstanceGrid = ({ stack, prUrlSet, koUrlSet, activeInstanceIndex, onSelectInstance }) => {
  const gridRef = useRef(null);
  const [twoColumns, setTwoColumns] = useState(false);

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return undefined;
    const measure = () => setTwoColumns(el.clientWidth >= TWO_COLUMN_MIN_WIDTH);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={gridRef}
      className="instance-grid"
      style={{ gridTemplateColumns: twoColumns ? "1fr 1fr" : "1fr" }}
      data-testid="instance-grid"
    >
      {stack.imageIds.map((imageId, i) => {
        const frames = Number(stack.images?.[i]?.numberOfFrames) || 1;
        return (
          <InstanceTile
            key={imageId || i}
            imageId={imageId}
            number={i + 1}
            isClip={frames > 1}
            frames={frames}
            hasPR={prUrlSet.has(imageId) || !!stack.images?.[i]?.isPRPresent}
            hasKO={koUrlSet.has(imageId)}
            active={activeInstanceIndex === i}
            onClick={() => onSelectInstance(i)}
          />
        );
      })}
    </div>
  );
};

export default InstanceGrid;
